import apiClient from './client'
import { isRunningInRNWebView, supportsSnsAuthSession, openInAppBrowserViaRN } from '@/utils/rn-bridge'
import type { JoinStatus } from '@/constants/codeConstants'

/**
 * Club API
 * 동호회 관리 API
 */

// ========================================
// Types
// ========================================

/** 4차발주 p45 — DB/Java(ClubCodeConstants.CLUB_STATUS_*)에는 있으나 Vue 타입에만 빠져 있던
 *  폐쇄 상태를 추가한다(3계층 정합). CLOSE_PENDING=임시폐쇄(10일 유예) / CLOSED=완전폐쇄 */
export type ClubStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'CLOSE_PENDING' | 'CLOSED'
export type ClubType = 'WORK' | 'SCHOOL' | 'SOCIAL_NEW' | 'REGION' | 'ETC_NEW'

export interface Club {
  clubSeq: number
  clubCode: string
  clubName: string
  clubDesc: string
  clubRegion?: string
  clubType: ClubType
  clubStatus: ClubStatus
  clubLogo?: string
  clubImage?: string
  /** 동호회 대표이미지 fileInfoSeq — 썸네일 `/api/file/view/{seq}?w=` */
  clubImageSeq?: number
  establishDate: string
  memberCount: number
  /** 4차발주 A-4(p11): 실회원수 — 오프라인 실활동 회원수(총무 입력). 가입회원수(memberCount)와 별개 */
  realMemberCount?: number | null
  /** 4차발주 C-2(p33): 설립일 YYYYMMDD — 오프라인 실제 설립일. 개설일(establishDate)과 별개 */
  foundedDate?: string | null
  /** 4차발주 C-3(p33): 총무 연락처 메인/서브. 메인 번호는 contactPhone 재사용 */
  mainContactNm?: string | null
  mainContactRole?: string | null
  subContactNm?: string | null
  subContactTel?: string | null
  subContactRole?: string | null
  /** 4차발주 C-10(p38): 회비 주기 MONTHLY/ANNUAL. 금액은 monthlyFee 공용 */
  feeCycle?: string | null
  /** 진행예정 행사 건수 (PPT 41) */
  upcomingEventCount?: number
  maxMembers?: number
  monthlyFee?: number
  contactEmail?: string
  contactPhone?: string
  address?: string
  website?: string
  createdBy: string
  createdAt: string
  // 내 동호회 목록에서 사용되는 추가 필드
  presidentName?: string
  secretaryName?: string
  viceSecretaryName?: string
  // 닉네임(이름) 통합 표시
  presidentCombinedNickName?: string
  secretaryCombinedNickName?: string
  viceSecretaryCombinedNickName?: string
  joinDate?: string
  memberRole?: string
  myMemberRole?: string
  // 동호회 구독 설정 (CMS에서 관리, 'Y'/'N')
  useScorePersonal?: string
  useScoreRanking?: string
  isPaidClub?: string
  subscriptionStatus?: string
  trialEndDate?: string
  nextBillingDate?: string
  /** TRIAL(기간 내)·ACTIVE·GRACE 이면 true */
  subscriptionUsable?: boolean
  /** 소속 일반회원에게 동호회 콘텐츠 진입을 막아야 하면 true */
  memberAccessBlocked?: boolean
  /**
   * 가입 신청을 받을 수 있는가 — 가입 신청 API 와 같은 판정(2026-09-29 §12-1).
   * false 면 «가입하기»를 노출하지 않는다. 값이 없으면(구 서버·조회 경고) 열린 것으로 본다 — 최종 판정은 서버가 한다.
   * ⚠️ subscriptionUsable 과 다르다: 구독 기록이 «없는» 동호회는 기존 회원 이용은 열어 두고(9/10 결정) 가입만 막는다.
   */
  joinOpen?: boolean
}

export interface ClubDetail extends Club {
  rules?: string
  introduction?: string
  benefits?: string[]
  activities?: string[]
  gallery?: string[]
  feeAccount?: string
  feeBank?: string
  feeAccountHolder?: string
  monthlyFeeDesc?: string
  specialFee?: number
  specialFeeDesc?: string
  /** 총무 알림·푸시 표시명 REAL | NICK */
  managerNameType?: string
  /** @deprecated managerNameType 사용 */
  presidentNameType?: string
  joinPolicyCd?: string
  joinPwd?: string
  feePayDay?: string
  feeRemindType?: string
  clubCharacter?: string[]
  clubImageSeq?: number
  rulesFileSeq?: number
  rulesFileName?: string
}

export interface ClubListRequest {
  page: number
  pageSize: number
  clubType?: ClubType
  clubStatus?: ClubStatus
  searchKeyword?: string
  /** 비공개 동호회(JOIN_POLICY PASSWORD) 제외 — 메인/홈 추천 노출 전용 */
  excludePrivate?: boolean
  /**
   * 홈 «이런 동호회는 어때요?» 추천(2026-10-01 슬라이드 26) — 서버가 운영 중·공개·가입 가능(정원·구독)만,
   * 로그인 회원이면 가입 행(승인·승인 대기·거절·탈퇴)이 있는 동호회를 빼고 최신 개설순으로 준다. 켜면 clubStatus·excludePrivate 는 무시된다
   */
  recommend?: boolean
}

export interface ClubListResponse {
  items: Club[]
  totalCount: number
  totalPages: number
  currentPage: number
}

export interface ClubCreateRequest {
  clubName: string
  clubDesc?: string
  clubType?: string
  contactPhone?: string
  clubRegion?: string
  maxMembers?: number
  /** 4차발주 A-4(p11): 실회원수 — 동호회 만들기에서 총무가 입력 */
  realMemberCount?: number | null
  /** 4차발주 C-2(p33): 설립일 YYYYMMDD — 오프라인 실제 설립일. 개설일(establishDate)과 별개 */
  foundedDate?: string | null
  /** 4차발주 C-3(p33): 총무 연락처 메인/서브. 메인 번호는 contactPhone 재사용 */
  mainContactNm?: string | null
  mainContactRole?: string | null
  subContactNm?: string | null
  subContactTel?: string | null
  subContactRole?: string | null
  /** 4차발주 C-10(p38): 회비 주기 MONTHLY/ANNUAL. 금액은 monthlyFee 공용 */
  feeCycle?: string | null
  usePassword?: boolean
  password?: string
  clubCharacter?: string[]
  managerNameType?: string
  /** @deprecated managerNameType 사용 — 하위호환 */
  presidentNameType?: string
  feeBank?: string
  feeAccountHolder?: string
  feeAccount?: string
  monthlyFee?: number
  monthlyFeeDesc?: string
  specialFee?: number
  specialFeeDesc?: string
  feePayDay?: string
  feeRemindType?: string
  clubImageSeq?: number
  rulesFileSeq?: number
  /** 4차발주 C-6(p36): 생성 단계 소개 첨부 — temp 업로드된 fileInfoSeq 목록 */
  introAttachmentSeqs?: number[]
  designatedMembers?: DesignatedMemberInput[]
}

export interface ClubDraft {
  draftSeq: number
  userSeq: number
  step: string
  clubName: string
  payloadJson: string
  clubImageSeq?: number
  rulesFileSeq?: number
  lastRemindDate?: string
}

export interface DesignatedMemberInput {
  memberName: string
  gender?: string
  phone: string
  email?: string
  address?: string
  /** 생년월일 YYYYMMDD — 개설 뒤 «명부 추가» 도 저장한다(2026-10-01 §7-B4) */
  birthDate?: string
}

/** 명부 업로드 미리보기 — 2026-10-01 발주 9/30 슬라이드 9-④ */
export interface DesignatedRosterPreview {
  members: Array<DesignatedMemberInput & { joinState?: 'MEMBER' | 'APPLYING' | 'GOLCHONGBI' | null; rosterState?: 'NEW' | 'UPDATE' }>
  skipped: Array<{ row?: number; name?: string; reason: string }>
  summary: Record<string, number>
}

export interface ClubNameCheckResult {
  available: boolean
  clubName: string
}

export interface ClubUpdateRequest {
  clubName?: string
  clubDesc?: string
  /** 4차발주 A-4(p11): 실회원수 — 동호회 설정에서 수정 */
  realMemberCount?: number | null
  /** 4차발주 C-2(p33): 설립일 YYYYMMDD — 오프라인 실제 설립일. 개설일(establishDate)과 별개 */
  foundedDate?: string | null
  /** 4차발주 C-3(p33): 총무 연락처 메인/서브. 메인 번호는 contactPhone 재사용 */
  mainContactNm?: string | null
  mainContactRole?: string | null
  subContactNm?: string | null
  subContactTel?: string | null
  subContactRole?: string | null
  /** 4차발주 C-10(p38): 회비 주기 MONTHLY/ANNUAL. 금액은 monthlyFee 공용 */
  feeCycle?: string | null
  clubStatus?: ClubStatus
  clubLogo?: string
  clubImage?: string
  clubImageSeq?: number
  rulesFileSeq?: number
  maxMembers?: number | null
  monthlyFee?: number
  contactEmail?: string
  contactPhone?: string
  address?: string
  website?: string
  rules?: string
  introduction?: string
  clubRegion?: string
  clubCharacter?: string[]
  joinPolicyCd?: string
  joinPwd?: string
  managerNameType?: string
  /** @deprecated managerNameType 사용 */
  presidentNameType?: string
  usePassword?: boolean
  password?: string
  bankName?: string
  accountHolder?: string
  accountNumber?: string
  feeBank?: string
  feeAccountHolder?: string
  feeAccount?: string
  feePayDay?: string
  feeRemindType?: string
  monthlyFeeDesc?: string
  specialFee?: number
  specialFeeDesc?: string
}

export interface ClubJoinAnswer {
  questionSeq: number
  answerText?: string
  selectedOptionSeq?: number
}

export interface ClubJoinRequest {
  clubCode: string
  password?: string
  message?: string
  handicap?: number
  answers?: ClubJoinAnswer[]
  /** 회원정보에 없을 때만 — 서버가 가입과 함께 저장(성별 M/F 는 서버도 필수로 본다). 2026-10-01 발주처 통화 */
  gender?: string
  birthday?: string
}

export interface ClubJoinQuestion {
  questionSeq: number
  questionText: string
  questionType: 'TEXT' | 'CHOICE' | 'MULTIPLE'
  requiredYn: 'Y' | 'N'
  questionOrder: number
  optionList?: Array<{
    optionSeq: number
    optionText: string
    optionOrder: number
  }>
}

export interface ClubMember {
  userSeq: number
  memberUserSeq?: number
  clubMemberSeq?: number
  userName: string
  userNickname?: string
  userNick?: string
  combinedNickName?: string
  profileImage?: string
  profileUrl?: string
  memberGrade: string
  joinDate?: string
  joinApproveDate?: string
  role?: 'PRESIDENT' | 'MANAGER' | 'VICE_MANAGER' | 'SPECIAL' | 'MEMBER'
  memberRole?: 'PRESIDENT' | 'MANAGER' | 'VICE_MANAGER' | 'SPECIAL' | 'MEMBER'
  userTel?: string
  phone?: string
  userEmail?: string
  email?: string
  clubHandicap?: number
  handicap?: number
  birthDate?: string
  gender?: string
  age?: number
}

export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'GRACE_PERIOD' | 'SUSPENDED' | 'CANCELLED' | 'COMPLIMENTARY'
// 요금제 4종(2026-07-08): 월/12/24/36개월. HALF_YEARLY(6개월)는 폐지 레거시(호환용).
export type PlanType = 'MONTHLY' | 'YEARLY' | 'BIENNIAL' | 'TRIENNIAL' | 'HALF_YEARLY'

export interface ClubSubscription {
  subscriptionSeq: number
  clubSeq: number
  planType: PlanType
  status: SubscriptionStatus
  trialStartDate?: string
  trialEndDate?: string
  subscriptionStartDate?: string
  subscriptionEndDate?: string
  nextBillingDate?: string
  gracePeriodEndDate?: string
  isAutoRenew: string
  cancelledDate?: string
  cancelReason?: string
  remainingTrialDays?: number
  remainingSubscriptionDays?: number
  daysUntilNextBilling?: number
  paymentAvailable?: boolean
  paymentRequired?: boolean
  /** CARD | BILLING | CASH_USER | CASH_CLUB | MIXED 등 */
  payMethod?: string
  billingAnchorDay?: 1 | 15 | number
  mixedCashAmount?: number
  cashAccountType?: string
  paidConvertPending?: boolean
  paidConvertAgreeYn?: string
  lastConsentSeq?: number
  /**
   * 결제가 일어난 곳. APPLE·GOOGLE 은 인앱 구독이라 «앱에서 해지할 수 없다» —
   * 스토어가 서버측 해지 API 를 주지 않으므로 사용자가 스토어 설정에서 직접 꺼야 한다.
   * 비어 있으면 PG(NicePay) 결제다.
   */
  storePlatform?: string
  /** 2026-10-01 §7-G4 — 카드(빌링키) 등록 여부 · 카드로 청구하는데 카드가 없음(총무 이관 뒤 등). 키 자체는 응답에 싣지 않는다 */
  cardRegistered?: boolean
  cardRequired?: boolean
}

export type ConsentPurpose = 'REGISTER' | 'CHANGE_METHOD' | 'CHANGE_DAY' | 'PAID_CONVERT' | 'PRICE_CHANGE'

export interface SubscriptionConsentView {
  consentSeq: number
  clubSeq: number
  userSeq: number
  subscriptionSeq?: number
  purpose: ConsentPurpose | string
  purposeLabel?: string
  payMethod: string
  payMethodLabel?: string
  cardKind?: string
  cardLast4?: string
  bankName?: string
  accountNumber?: string
  accountHolder?: string
  mixedCashAccount?: string
  mixedAddonMethod?: string
  mixedCashAmount?: number
  amount?: number
  billingAnchorDay?: number
  termsVersion?: string
  snapshotJson?: string
  consumedYn: string
  consumedLabel?: string
  consentDate?: string
  stale?: boolean
  mismatchFields?: string[]
}

export interface SubscriptionConsentBundle {
  latest?: SubscriptionConsentView | null
  items: SubscriptionConsentView[]
  stale: boolean
  mismatchFields: string[]
  updatePurpose?: ConsentPurpose | string | null
}

export interface ClubPayment {
  paymentSeq: number
  subscriptionSeq: number
  clubSeq: number
  paymentType: 'INITIAL' | 'RENEWAL' | 'UPGRADE'
  billingPeriodStart: string
  billingPeriodEnd: string
  amount: number
  discountAmount: number
  finalAmount: number
  paymentStatus: 'PENDING' | 'SUCCESS' | 'FAILED' | 'REFUNDED'
  pgTid?: string
  paidAt?: string
  failedAt?: string
  failureReason?: string
  refundedAt?: string
  refundAmount?: number
  refundReason?: string
}

export interface ClubPlan {
  planType: PlanType
  name: string
  price: number
  originalPrice?: number
  period: number
  description: string
  firstDealMonths?: number | null
  firstDealEnabled?: boolean | null
}

// ========================================
// API Client
// ========================================

/**
 * 4차발주 C-6: 동호회 소개 첨부 (tb_file_info · MDI_TYPE=CLUB_INTRO)
 * gubun — 'IMAGE'(소개 하단 썸네일 노출) / 'FILE'(다운로드 링크 노출)
 */
/** 골총비 기본 대표 이미지 — GET /api/club/default-images */
export interface ClubDefaultImage {
  fileInfoSeq: number
  url: string
  name?: string
}

export interface ClubIntroAttachment {
  fileInfoSeq: number
  gubun: 'IMAGE' | 'FILE'
  fileOriNm: string
  fileExt?: string
  fileSize?: number
  regDate?: string
}

export const clubApi = {
  /**
   * 동호회 목록 조회
   * @param params 검색 조건
   * @returns 동호회 목록
   */
  getClubs: async (params: ClubListRequest) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubListResponse
    }>('/api/club/list', { params })
    return response.data
  },

  /**
   * 동호회 상세 조회
   * @param clubCode 동호회 인코딩 코드
   * @returns 동호회 상세 정보
   */
  getClub: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubDetail
    }>(`/api/club/${clubCode}`)
    return response.data
  },

  /**
   * 동호회 생성
   * @param data 동호회 데이터
   */
  createClub: async (data: ClubCreateRequest) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data: { clubSeq: number; clubCode: string }
    }>('/api/club', data)
    return response.data
  },

  /**
   * 동호회명 중복 확인
   * @param clubName 동호회명
   */
  checkClubName: async (clubName: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubNameCheckResult
    }>('/api/club/check-name', {
      params: { clubName },
    })
    return response.data
  },

  /**
   * 동호회 수정
   * @param clubCode 동호회 인코딩 코드
   * @param data 수정할 데이터
   */
  updateClub: async (clubCode: string, data: ClubUpdateRequest) => {
    const response = await apiClient.put<{
      code: number
      message: string
    }>(`/api/club/${clubCode}`, data)
    return response.data
  },

  /**
   * 동호회 삭제
   * @param clubCode 동호회 인코딩 코드
   */
  deleteClub: async (clubCode: string) => {
    const response = await apiClient.delete<{
      code: number
      message: string
    }>(`/api/club/${clubCode}`)
    return response.data
  },

  // ─── 동호회 폐쇄 + 환불 (B-06) ───────────────────────
  // 폐쇄 신청 (총무): 상태전환 + 구독해지 + 지갑동결 + 잔액>0이면 환불신청 + 환불정산 링크 발송
  closeClub: async (
    clubCode: string,
    payload: {
      closeType: 'IMMEDIATE' | 'GRACE'
      refundBank?: string
      refundAccount?: string
      refundHolder?: string
      agreeYn: 'Y' | 'N'
    }
  ) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data?: { refundRequested?: boolean; settleToken?: string; settleUrl?: string }
    }>(`/api/club/${clubCode}/close`, payload)
    return response.data
  },
  /** 환불정산 페이지 조회 (토큰) — PPT 37·38 */
  getCloseSettlement: async (token: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data?: Record<string, unknown>
    }>('/api/club/close-settlement', { params: { token } })
    return response.data
  },
  /** 환불정산 총무 확정 */
  confirmCloseSettlement: async (payload: { token: string; agreeYn: 'Y' | 'N' }) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      '/api/club/close-settlement/confirm',
      payload
    )
    return response.data
  },
  // 재활성 (임시폐쇄/폐쇄 → 활성): 지갑 동결 해제 + 승인 전 환불 신청 취소
  reactivateClub: async (clubCode: string) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      `/api/club/${clubCode}/reactivate`
    )
    return response.data
  },

  /**
   * 동호회 가입 신청
   * @param data 가입 신청 데이터
   */
  joinClub: async (data: ClubJoinRequest) => {
    const response = await apiClient.post<{
      code: number
      message: string
      /** 승인제 동호회면 pendingApproval=true (2026-09-03) — 화면이 안내와 이동을 갈라야 한다 */
      data?: { joinStatus?: string; pendingApproval?: boolean }
    }>('/api/club/join', data)
    return response.data
  },

  /**
   * 내 가입 상태 (발주 2026-09-29 슬라이드 20) — 가입 화면이 신청서를 그리기 전에 먼저 본다.
   * `getMyClubs`(/api/club/my)는 승인 회원만 돌려줘 승인 대기·거절을 구분할 수 없다.
   * GET /api/club/{clubCode}/my-join-status (로그인 필요)
   * @param clubCode 동호회 인코딩 코드
   */
  getMyJoinStatus: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      /** status: NONE(가입 이력 없음·탈퇴) | PENDING | APPROVED | REJECTED — 값은 JOIN_STATUS 상수 */
      data?: { status: 'NONE' | JoinStatus; joinRequestDate?: string | null }
    }>(`/api/club/${clubCode}/my-join-status`)
    return response.data
  },

  /**
   * 동호회 가입질문 목록 조회
   * @param clubCode 동호회 인코딩 코드
   */
  getJoinQuestions: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubJoinQuestion[]
    }>(`/api/club/${clubCode}/join-questions`)
    return response.data
  },

  /**
   * 동호회 가입질문 일괄 저장 (총무용)
   * @param clubCode 동호회 인코딩 코드
   * @param questions 질문 배열
   */
  saveJoinQuestions: async (clubCode: string, questions: Array<{
    question: string
    type: 'OPEN' | 'SUBJECTIVE'
    required: boolean
    allowMultiple: boolean
    choices: string[]
  }>) => {
    const response = await apiClient.post<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/join-questions`, questions)
    return response.data
  },

  /**
   * 동호회 탈퇴
   * @param clubCode 동호회 인코딩 코드
   */
  leaveClub: async (clubCode: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/leave`)
    return response.data
  },

  /**
   * 내가 가입한 동호회 목록
   */
  /** 회원 «특별사항» — 총무·부총무 전용(2026-10-03 10/2본 94·96번 장) */
  getMemberSpecialNotes: async (clubCode: string, clubMemberSeq: number) => {
    const response = await apiClient.get<{ code: number; message: string; data: MemberSpecialNotes }>(
      `/api/club/${clubCode}/members/${clubMemberSeq}/special-notes`)
    return response.data
  },
  /** 같은 «특별사항» 을 사용자 번호로 — 조편성·참가자현황 회원정보(ⓘ) */
  getMemberSpecialNotesByUser: async (clubCode: string, userSeq: number) => {
    const response = await apiClient.get<{ code: number; message: string; data: MemberSpecialNotes }>(
      `/api/club/${clubCode}/member-special-notes`, { params: { userSeq } })
    return response.data
  },
  /** 총무 «특별참고사항» 쓰기·고치기·지우기 — 93번 장 <1> */
  addMemberNote: async (clubCode: string, clubMemberSeq: number, content: string) => {
    const response = await apiClient.post<{ code: number; message: string }>(`/api/club/${clubCode}/members/${clubMemberSeq}/notes`, { content })
    return response.data
  },
  updateMemberNote: async (clubCode: string, clubMemberSeq: number, noteSeq: number, content: string) => {
    const response = await apiClient.put<{ code: number; message: string }>(`/api/club/${clubCode}/members/${clubMemberSeq}/notes/${noteSeq}`, { content })
    return response.data
  },
  deleteMemberNote: async (clubCode: string, clubMemberSeq: number, noteSeq: number) => {
    const response = await apiClient.delete<{ code: number; message: string }>(`/api/club/${clubCode}/members/${clubMemberSeq}/notes/${noteSeq}`)
    return response.data
  },
  /** 행사 참가 특별요청 한 건 지우기(총무·부총무) — 10/6본 118번 장 */
  deleteMemberEventRequest: async (clubCode: string, clubMemberSeq: number, participantSeq: number) => {
    const response = await apiClient.delete<{ code: number; message: string }>(
      `/api/club/${clubCode}/members/${clubMemberSeq}/event-requests/${participantSeq}`)
    return response.data
  },
  /** 가입 특별요청 지우기(총무·부총무) — 10/6본 118번 장 */
  deleteMemberJoinRequest: async (clubCode: string, clubMemberSeq: number) => {
    const response = await apiClient.delete<{ code: number; message: string }>(`/api/club/${clubCode}/members/${clubMemberSeq}/join-request`)
    return response.data
  },
  /** 내 가입 특별요청 고치기·지우기(본인) — 10/6본 118번 장 */
  updateMyJoinRequest: async (clubCode: string, content: string) => {
    const response = await apiClient.put<{ code: number; message: string }>(`/api/club/${clubCode}/my-join-request`, { content })
    return response.data
  },
  deleteMyJoinRequest: async (clubCode: string) => {
    const response = await apiClient.delete<{ code: number; message: string }>(`/api/club/${clubCode}/my-join-request`)
    return response.data
  },
  /** 마이페이지 «동호회 가입현황» — 98번 장 */
  getMyClubDashboard: async () => {
    const response = await apiClient.get<{ code: number; message: string; data: MyClubDashboardItem[] }>('/api/club/my/dashboard')
    return response.data
  },

  getMyClubs: async () => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: Club[]
    }>('/api/club/my')
    return response.data
  },

  /**
   * 내가 가입 신청 중인(승인 대기) 동호회 — 2026-10-01 슬라이드 27 «신청중».
   * ⛔ getMyClubs 와 합쳐 권한(stores/auth.ts)에 쓰지 않는다 — 신청자는 회원이 아니다
   */
  getMyPendingClubs: async () => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: Club[]
    }>('/api/club/my/pending')
    return response.data
  },

  /** 동호회 만들기 임시저장(draft) — 저장/갱신 (수정계획 M-6) */
  saveDraft: async (data: { step: string; clubName?: string; payload: unknown; clubImageSeq?: number; rulesFileSeq?: number }) => {
    const response = await apiClient.post<{ code: number; message: string; data: unknown }>('/api/club/draft', data)
    return response.data
  },

  /** 내 진행중 draft 조회 (없으면 data=null) */
  getDraft: async () => {
    const response = await apiClient.get<{ code: number; message: string; data: ClubDraft | null }>('/api/club/draft')
    return response.data
  },

  /** draft 폐기 (생성 완료/취소) */
  deleteDraft: async () => {
    const response = await apiClient.delete<{ code: number; message: string }>('/api/club/draft')
    return response.data
  },

  /** 지정회원 명부 파일(.xlsx/.docx) 파싱 — 미저장 미리보기 (수정계획 M-5) */
  parseRoster: async (file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<{ code: number; message: string; data: DesignatedMemberInput[] }>(
      '/api/club/designated-members/parse', form, { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  /** 지정회원 목록 (사후관리) */
  getDesignatedMembers: async (clubCode: string, status?: string) => {
    const response = await apiClient.get<{ code: number; message: string; data: any[] }>(
      `/api/club/${clubCode}/designated-members`, { params: status ? { status } : {} }
    )
    return response.data
  },

  /** 명부 업로드 미리보기(저장 안 함) — 인식·제외(사유)·새로/갱신·가입 상태 (2026-10-01 슬라이드 9-④) */
  previewDesignatedMembers: async (clubCode: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<{ code: number; message: string; data: DesignatedRosterPreview }>(
      `/api/club/${clubCode}/designated-members/preview`, form, { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  /** 지정회원 다건 저장 — consentConfirmed: 명부 속 분들의 동의를 받았음(슬라이드 11 결정 ④ — 서버도 확인한다) */
  saveDesignatedMembers: async (clubCode: string, members: DesignatedMemberInput[], consentConfirmed = false) => {
    const response = await apiClient.post<{ code: number; message: string; data: number }>(
      `/api/club/${clubCode}/designated-members`, { members, consentConfirmed }
    )
    return response.data
  },

  /** 지정회원 수신거부 표시·해제 — 표시된 사람은 가입요청 알림·알림스튜디오 발송에서 빠진다(2026-10-01 슬라이드 11 ③) */
  setDesignatedOptOut: async (clubCode: string, seq: number, optOut: boolean) => {
    const response = await apiClient.patch<{ code: number; message: string }>(
      `/api/club/${clubCode}/designated-members/${seq}/opt-out`, { optOut }
    )
    return response.data
  },

  /** 지정회원 삭제 */
  deleteDesignatedMember: async (clubCode: string, seq: number) => {
    const response = await apiClient.delete<{ code: number; message: string }>(
      `/api/club/${clubCode}/designated-members/${seq}`
    )
    return response.data
  },

  /** 지정회원 가입 전 알림(SMS) 발송 */
  notifyDesignatedMembers: async (clubCode: string, memberSeqs?: number[], message?: string) => {
    const response = await apiClient.post<{ code: number; message: string; data: number }>(
      `/api/club/${clubCode}/designated-members/notify`, { memberSeqs, message }
    )
    return response.data
  },

  /**
   * 동호회 회원 목록
   * @param clubCode 동호회 인코딩 코드
   */
  getClubMembers: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubMember[]
    }>(`/api/club/${clubCode}/members`)
    return response.data
  },

  /**
   * 동호회 통계
   * @param clubCode 동호회 인코딩 코드
   */
  getClubStatistics: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        memberCount: number
        eventCount: number
        totalRevenue: number
        monthlyRevenue: number
        activeMembers: number
      }
    }>(`/api/club/${clubCode}/statistics`)
    return response.data
  },

  /**
   * 동호회 순위 목록 조회 (가입회원수 + 행사수 기준)
   * @param limit 가져올 개수 (기본 5)
   */
  getClubRanking: async (limit: number = 5) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        items: Array<{
          clubSeq: number
          clubName: string
          clubDesc?: string
          clubType?: string
          memberCount: number
          eventCount: number
          rankScore: number
          profileImageUrl?: string
        }>
        count: number
      }
    }>('/api/club/ranking', { params: { limit } })
    return response.data
  },

  /**
   * 동호회 가입 비밀번호 검증
   * @param clubCode 동호회 인코딩 코드
   * @param password 비밀번호
   */
  verifyPassword: async (clubCode: string, password: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data: { verified: boolean }
    }>(`/api/club/${clubCode}/verify-password`, { password })
    return response.data
  },

  // ========================================
  // 구독/결제 API
  // ========================================

  /**
   * 결제 준비 (결제창 열기 전)
   * @param clubCode 동호회 인코딩 코드
   * @param planType 요금제 타입 (MONTHLY/YEARLY)
   */
  preparePayment: async (clubCode: string, planType: string, payMethod?: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data: {
        clubSeq: number
        planType: string
        amount: number
        paymentId?: string
        orderName?: string
        windowUrl?: string
        nicepayParams?: Record<string, string>
        changed?: boolean
      }
    }>('/api/club/subscription/prepare', { clubCode, planType, payMethod })
    return response.data
  },

  /**
   * 정기결제 빌링키 등록창 준비 (B-1/B-2) — payMethod BILLING/MIXED, 혼합 시 cashAccountType=CLUB
   * @returns { windowUrl } 팝업으로 열 빌링 등록창
   */
  prepareBilling: async (
    clubCode: string,
    planType: string,
    payMethod: string,
    cashAccountType?: string,
    agreeAutoPayment?: boolean,
    autoPayTermsVersion?: string,
    mixedCashAmount?: number,
    billingAnchorDay?: 1 | 15 | null,
    consentSeq?: number,
  ) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data?: { windowUrl?: string; planType?: string }
    }>(`/api/club/subscription/${clubCode}/prepare-billing`, {
      planType, payMethod, cashAccountType, agreeAutoPayment, autoPayTermsVersion, mixedCashAmount, billingAnchorDay, consentSeq,
    })
    return response.data
  },

  /**
   * 정기 골총캐쉬 recurring 등록 (B-2 §4-D) — 즉시 과금 없이 2개월 무료 후 월 캐쉬 차감
   */
  registerRecurringCash: async (
    clubCode: string,
    planType: string,
    cashAccountType: 'USER' | 'CLUB',
    agreeAutoPayment?: boolean,
    autoPayTermsVersion?: string,
    billingAnchorDay?: 1 | 15 | null,
    consentSeq?: number,
  ) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data?: { planType?: string; payMethod?: string }
    }>(`/api/club/subscription/${clubCode}/register-recurring-cash`, {
      planType, cashAccountType, agreeAutoPayment, autoPayTermsVersion, billingAnchorDay, consentSeq,
    })
    return response.data
  },

  submitConsent: async (clubCode: string, body: Record<string, unknown>) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data?: { consentSeq?: number }
    }>(`/api/club/subscription/${clubCode}/consent`, body)
    return response.data
  },

  listConsent: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data?: SubscriptionConsentBundle
    }>(`/api/club/subscription/${clubCode}/consent`)
    return response.data
  },

  getConsent: async (clubCode: string, consentSeq: number) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data?: SubscriptionConsentView
    }>(`/api/club/subscription/${clubCode}/consent/${consentSeq}`)
    return response.data
  },

  applyConsent: async (clubCode: string, consentSeq: number) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      `/api/club/subscription/${clubCode}/consent/apply`,
      { consentSeq },
    )
    return response.data
  },

  changeBillingDay: async (clubCode: string, billingAnchorDay: 1 | 15, consentSeq: number) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      `/api/club/subscription/${clubCode}/change-billing-day`,
      { billingAnchorDay, consentSeq },
    )
    return response.data
  },

  agreePaidConvert: async (clubCode: string, consentSeq: number) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      `/api/club/subscription/${clubCode}/agree-paid-convert`,
      { consentSeq },
    )
    return response.data
  },

  /**
   * 결제 완료 처리
   * @param clubCode 동호회 인코딩 코드
   * @param planType 요금제 타입
   * @param impUid PortOne 결제 고유 ID (선택)
   * @param merchantUid 상점 주문 ID (선택)
   */
  completePayment: async (
    clubCode: string,
    planType: string,
    impUid?: string,
    merchantUid?: string
  ) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data: {
        success: boolean
        subscriptionSeq: number
        paymentSeq: number
        status: string
      }
    }>('/api/club/subscription/complete', {
      clubCode,
      planType,
      impUid,
      merchantUid
    })
    return response.data
  },

  /**
   * 구독 조회
   * @param clubCode 동호회 인코딩 코드
   */
  getSubscription: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubSubscription
    }>(`/api/club/subscription/${clubCode}`)
    return response.data
  },

  /**
   * 결제 이력 조회
   * @param clubCode 동호회 인코딩 코드
   */
  getPaymentHistory: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        items: ClubPayment[]
        totalCount: number
      }
    }>(`/api/club/subscription/${clubCode}/payments`)
    return response.data
  },

  /**
   * 구독 해지
   * @param clubCode 동호회 인코딩 코드
   * @param reason 해지 사유 (선택)
   */
  cancelSubscription: async (clubCode: string, reason?: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data?: {
        refundRequested?: boolean
        refundAmount?: number
        refundType?: string
        accessUntilEnd?: boolean
        accessEndDate?: string
      }
    }>(`/api/club/subscription/${clubCode}/cancel`, { reason })
    return response.data
  },

  /**
   * 요금제 정보 조회
   */
  getPlans: async () => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        plans: ClubPlan[]
        pgScreening?: boolean
        /** PG 판매 조건(24·36개월·혜택 문구 숨김) — 2026-09-29 */
        pgRequirements?: boolean
        trialMonths?: number
        firstDealMonths?: number | null
        firstDealEnabled?: boolean | null
        freeBenefitText?: string | null
        cardOnly?: boolean
      }
    }>('/api/club/subscription/plans')
    return response.data
  },

  /**
   * 동호회별 구독 가격 정보 조회 (관리자 설정·할인율 반영)
   * @param clubCode 동호회 인코딩 코드
   */
  getPricingInfo: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        // 요금제 4종(2026-07-08): 서버 /pricing-info 가 planType.toLowerCase()+'Price' 키로 내려줌
        monthlyPrice: number
        yearlyPrice: number
        biennialPrice: number
        triennialPrice: number
        currentPlanType: string | null
        currentStatus: string | null
        subscriptionEndDate: string | null
        nextBillingDate: string | null
        pgScreening?: boolean
        /** PG 판매 조건(24·36개월·혜택 문구 숨김) — 2026-09-29 */
        pgRequirements?: boolean
        trialMonths?: number
        firstDealMonths?: number | null
        firstDealEnabled?: boolean | null
        freeBenefitText?: string | null
        cardOnly?: boolean
      }
    }>(`/api/club/subscription/${clubCode}/pricing-info`)
    return response.data
  },

  /**
   * 구독 플랜 변경
   * @param clubCode 동호회 인코딩 코드
   * @param newPlanType 새 요금제 ('MONTHLY' | 'HALF_YEARLY' | 'YEARLY')
   * @returns changed=true → 즉시 변경, changed=false → windowUrl로 결제 필요
   */
  changePlan: async (clubCode: string, newPlanType: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data: {
        changed: boolean
        priceDiff: number
        newPlanType: string
        newPlanPrice: number
        windowUrl?: string
      }
    }>(`/api/club/subscription/${clubCode}/change-plan`, { newPlanType })
    return response.data
  },

  /**
   * 앱 인앱결제(IAP) 구독 검증·활성화 — 앱(RN)에서만. 월/연만 지원(24/36개월은 웹 전용).
   * 서버 iap.enabled 미개통 시 503(준비중).
   */
  verifyIapSubscription: async (
    clubCode: string,
    payload: { platform: 'APPLE' | 'GOOGLE'; productId: string; storeTxId: string; receipt: string },
  ) => {
    const response = await apiClient.post<{ code: number; message: string; data?: { planType: string } }>(
      `/api/club/subscription/${clubCode}/iap-verify`, payload,
    )
    return response.data
  },

  // ========================================
  // 운영진 전용 API (총무/부총무/회장)
  // ========================================

  /**
   * 가입 신청 목록 조회
   * @param clubCode 동호회 인코딩 코드
   * @param status 상태 (PENDING: 대기중, PROCESSED: 처리완료)
   * @param page 페이지 번호
   */
  getJoinRequests: async (clubCode: string, status: string, page: number = 1) => {
    const response = await apiClient.get<{ code: number; message: string; data: any }>(
      `/api/club/${clubCode}/join-requests`,
      { params: { status, page } },
    )
    // 백엔드는 [{ member, answers }] 배열을 반환 → 화면이 기대하는 { items, totalCount, totalPages } 평면 구조로 정규화
    // (이전: 페이지가 res.data.items 를 기대했으나 배열이 와서 항상 빈 목록으로 표시되던 버그 수정 — 2026-06-05)
    const raw = response.data
    const d: any = raw?.data
    const arr: any[] = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : []
    const fmt = (s?: string) => (s && /^\d{14}$/.test(s)) ? `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}` : (s || '')
    const items = arr.map((it: any) => {
      const m = it?.member ?? it ?? {}
      return {
        requestSeq: m.clubMemberSeq ?? m.requestSeq,
        userSeq: m.userSeq,
        userName: m.userName,
        combinedNickName: m.combinedNickName,
        userTel: m.userTel,
        requestDate: fmt(m.joinRequestDate ?? m.requestDate),
        processedDate: fmt(m.joinApproveDate ?? m.processedDate),
        status: m.joinStatus ?? m.status,
        message: m.joinGreeting ?? m.message,
        rejectReason: m.rejectReason,
        answers: it?.answers ?? m.answers ?? [],
      }
    })
    return {
      code: raw?.code,
      message: raw?.message,
      data: { items, totalCount: d?.totalCount ?? items.length, totalPages: d?.totalPages ?? 1 },
    }
  },

  /**
   * 가입 승인
   * @param clubCode 동호회 인코딩 코드
   * @param requestSeq 신청 일련번호
   */
  approveJoinRequest: async (clubCode: string, requestSeq: number) => {
    const response = await apiClient.post<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/join-requests/${requestSeq}/approve`)
    return response.data
  },

  /**
   * 가입 거절
   * @param clubCode 동호회 인코딩 코드
   * @param requestSeq 신청 일련번호
   * @param reason 거절 사유
   */
  rejectJoinRequest: async (clubCode: string, requestSeq: number, reason?: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/join-requests/${requestSeq}/reject`, { reason })
    return response.data
  },

  /**
   * 회비 현황 조회
   * @param clubCode 동호회 인코딩 코드
   * @param params 검색 조건
   */
  getFeeStatus: async (clubCode: string, params: { month: string; filter: string; page: number; targetUserSeq?: number }) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        items: Array<{
          feeSeq: number
          userSeq: number
          userName: string
          combinedNickName?: string
          roleName?: string
          memberGrade: string
          feeAmount?: number
          isPaid: boolean
          paymentStatus: string
          paidDate?: string
          paymentDate?: string
          amount?: number
        }>
        paidCount: number
        unpaidCount: number
        totalAmount: number
        balanceAmount: number
        unpaidAmount: number
        feeAmount: number
        feePayDay: number
        memberCount: number
        totalPages: number
      }
    }>(`/api/club/${clubCode}/fee/status`, { params })
    return response.data
  },

  /**
   * 회비 납부 상태 일괄 저장
   */
  updateFeePaymentBatch: async (clubCode: string, items: Array<{
    feeSeq: number
    paymentStatus: string
    feeAmount?: number
  }>) => {
    const response = await apiClient.put<{
      code: number
      message: string
      data: number
    }>(`/api/club/${clubCode}/fee/payment-batch`, items)
    return response.data
  },

  /**
   * 회비 엑셀 업로드 처리
   */
  uploadFeeExcel: async (clubCode: string, file: File, month: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('month', month)
    const response = await apiClient.post<{
      code: number
      message: string
      data: { updated: number; skipped: number }
    }>(`/api/club/${clubCode}/fee/excel-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  /**
   * 회비 엑셀 다운로드
   */
  /**
   * 납부현황 엑셀 다운로드
   * 4차발주 F-8·F-10(p111·p112): `from`·`to`(YYYY-MM)를 주면 **기간 조회분**을 내려받는다.
   * 둘 다 없으면 기존 단월(`month`) 동작.
   */
  /**
   * 4차발주 p111(녹색 요구) — 회비 납부현황 엑셀 **전송**
   * 다운로드와 같은 엑셀을 만들어 MMS 첨부로 보낸다.
   * 기본 수신처는 운영진이며, 전 회원 발송은 toAllMembers 를 명시해야 한다(개인정보).
   */
  sendFeeExcel: async (
    clubCode: string,
    body: {
      month?: string; from?: string; to?: string; content?: string; toAllMembers?: boolean
      /** 4차발주 p110: 전송수단 — 'SMS'(기본, 첨부 있어 MMS 승격) | 'EMAIL' */
      channel?: 'SMS' | 'EMAIL'
      /** 4차발주 p110: 개별 수신자. 지정 시 서버가 해당 동호회 회원인지 전수 검증한다 */
      recipientUserSeqs?: number[]
    },
  ) => {
    const response = await apiClient.post<{
      code: number
      message: string
      data?: { fileName: string; toAllMembers: boolean; channel?: string; recipientCount?: number | null }
    }>(`/api/club/${clubCode}/fee/excel-send`, body)
    return response.data
  },

  /**
   * 회비 엑셀 내려받기.
   * @returns 'downloaded'(웹: 파일 저장) · 'opened'(앱: 인앱 브라우저 시트로 서버 파일을 엶) ·
   *          'unsupported'(구버전 앱: WebView 가 blob 다운로드를 못 한다 — 거짓 «다운로드했습니다» 방지)
   *
   * ⛔ 앱 WebView 는 blob: + a.download 를 처리하지 못한다(react-native-webview 가 blob URI 를 조용히 중단).
   *    새 앱은 서버 URL(accessToken 쿼리 — JwtAuthenticationFilter 허용)을 시트로 열어 받는다(2026-09-27).
   */
  downloadFeeExcel: async (
    clubCode: string, month: string, range?: { from: string; to: string },
  ): Promise<'downloaded' | 'opened' | 'unsupported'> => {
    const params = range ? { from: range.from, to: range.to } : { month }
    if (isRunningInRNWebView()) {
      if (!supportsSnsAuthSession()) return 'unsupported'
      const qs = new URLSearchParams(
        range ? { from: range.from, to: range.to } : { month },
      )
      qs.set('accessToken', localStorage.getItem('accessToken') || '')
      const base = String(apiClient.defaults.baseURL || '').replace(/\/$/, '')
      openInAppBrowserViaRN(`${base}/api/club/${clubCode}/fee/excel?${qs.toString()}`)
      return 'opened'
    }
    const response = await apiClient.get(`/api/club/${clubCode}/fee/excel`, {
      params,
      responseType: 'blob'
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    const suffix = range
      ? `${range.from.replace('-', '')}-${range.to.replace('-', '')}`
      : month.replace('-', '')
    link.setAttribute('download', `회비관리_${suffix}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    return 'downloaded'
  },

  /**
   * 4차발주 F-8·F-10(p111·p112): 회비납부현황 **기간 조회**(분기·반기·연간 등)
   * 단월 편집 화면과 달리 회원별 누계를 돌려주는 읽기 전용 리뷰다.
   */
  getFeePeriodSummary: async (clubCode: string, from: string, to: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data?: {
        from: string
        to: string
        items: Array<{
          userSeq: number
          displayName: string
          roleName?: string
          paidAmount: number
          unpaidAmount: number
          donationAmount: number
          paidCount: number
          unpaidCount: number
          exceptionCount: number
          donationCount: number
        }>
        memberCount: number
        recordCount: number
        paidAmount: number
        unpaidAmount: number
        donationAmount: number
        paidCount: number
        unpaidCount: number
        exceptionCount: number
        donationCount: number
      }
    }>(`/api/club/${clubCode}/fee/period`, { params: { from, to } })
    return response.data
  },

  /**
   * 회비 설정 조회
   * @param clubCode 동호회 인코딩 코드
   */
  getFeeSettings: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        monthlyFee: number
        payDay: string
        bankName: string
        accountNumber: string
        accountHolder: string
        paymentGuide: string
        enableReminder: boolean
      }
    }>(`/api/club/${clubCode}/fee/settings`)
    return response.data
  },

  /**
   * 회비 설정 수정
   * @param clubCode 동호회 인코딩 코드
   * @param settings 회비 설정
   */
  updateFeeSettings: async (clubCode: string, settings: {
    monthlyFee: number
    payDay: string
    bankName: string
    accountNumber: string
    accountHolder: string
    paymentGuide: string
    enableReminder: boolean
  }) => {
    const response = await apiClient.put<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/fee/settings`, settings)
    return response.data
  },

  /**
   * 납부 기록 조회
   * @param clubCode 동호회 인코딩 코드
   * @param page 페이지 번호
   */
  getFeeHistory: async (clubCode: string, page: number = 1) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        items: Array<{
          historySeq: number
          userSeq: number
          userName: string
          feeType: string
          amount: number
          paidDate: string
          memo?: string
        }>
        totalPages: number
      }
    }>(`/api/club/${clubCode}/fee/history`, { params: { page } })
    return response.data
  },

  /**
   * 내 회비 조회 (본인 회비만 — self-scope). 회비 납부 탭용. (#3)
   * filter 미지정 시 전체 → 프론트에서 미납(UNPAID/OVERDUE)·납부(PAID) 구분.
   */
  getMyFees: async (clubCode: string, filter?: 'UNPAID' | 'PAID') => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: {
        items: Array<{
          feeSeq: number
          feeType: string
          feeAmount: number
          paymentStatus: string
          feeYear?: number
          feeMonth?: number
          paymentDate?: string
        }>
      }
    }>(`/api/club/${clubCode}/fee/my`, { params: filter ? { filter } : {} })
    return response.data
  },

  /**
   * 회원 역할 변경 (운영진)
   */
  updateMemberRole: async (
    clubCode: string,
    targetUserSeq: number,
    memberRole: 'MEMBER' | 'MANAGER' | 'VICE_MANAGER' | 'PRESIDENT'
  ) => {
    const response = await apiClient.put<{
      code: number
      message: string
      data?: { transferSeq?: number }
    }>(`/api/club/${clubCode}/members/${targetUserSeq}/role`, { memberRole })
    return response.data
  },

  // ─── 총무 이관 (B-07) ───────────────────────────────
  // 내게 온 진행중 이관 요청 (신임 총무 관점)
  getMyPendingTransfer: async () => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: Array<{
        transferSeq: number
        clubSeq: number
        clubName: string
        fromUserName: string
        toUserName: string
        requestDate: string
        expireDate: string
      }>
    }>(`/api/club/manager-transfer/pending`)
    return response.data
  },
  // 신임 총무 수락 (본인확인 완료 후)
  // ⛔ 2026-09-28 — «본인확인 했음(ciVerified:true)» 을 보내지 않는다. 서버가 인증 결과 토큰(1회용)으로 확인한다
  acceptManagerTransfer: async (transferSeq: number, certToken: string) => {
    const response = await apiClient.post<{
      code: number
      message: string
      /** subscriptionAction — BILLING_REREG_REQUIRED(전임 카드 해지 → 신임 카드 등록) · STORE_CANCEL_GUIDE(인앱) 등 · clubCode — 2026-10-01 §7-G4 */
      data?: { subscriptionAction?: string; clubCode?: string }
    }>(`/api/club/manager-transfer/${transferSeq}/accept`, { certToken })
    return response.data
  },
  // 신임 총무 거부
  rejectManagerTransfer: async (transferSeq: number) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      `/api/club/manager-transfer/${transferSeq}/reject`
    )
    return response.data
  },
  // 전임 총무 지명 철회
  cancelManagerTransfer: async (transferSeq: number) => {
    const response = await apiClient.post<{ code: number; message: string }>(
      `/api/club/manager-transfer/${transferSeq}/cancel`
    )
    return response.data
  },

  /**
   * 선택정보 (동호회 가입 질문/답변) 조회
   * @param clubCode 동호회 인코딩 코드
   * @param clubMemberSeq
   */
  getMemberJoinQna: async (clubCode: string, clubMemberSeq: number) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: Array<{
        answerSeq:           number
        questionSeq:         number
        questionText:        string
        questionType:        'TEXT' | 'CHOICE' | 'MULTIPLE'
        requiredYn:          'Y' | 'N'
        answerText:          string
        selectedOptionSeq?:  number
        selectedOptionSeqs?: number[]
        optionList?: Array<{
          optionSeq:   number
          optionText:  string
          optionOrder: number
        }>
      }>
    }>(`/api/club/${clubCode}/members/${clubMemberSeq}/join-qna`)
    return response.data.data
  },

  /**
   * 선택정보 (동호회 가입 질문/답변) 수정
   * @param clubCode 동호회 인코딩 코드
   * @param clubMemberSeq
   * @param answers 수정할 답변 목록
   */
  updateMemberJoinQna: async (
    clubCode: string,
    clubMemberSeq: number,
    answers: Array<{
      questionSeq:        number
      answerText?:        string
      selectedOptionSeq?: number
    }>
  ) => {
    const response = await apiClient.put<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/members/${clubMemberSeq}/join-qna`, { answers })
    return response.data
  },

  /**
   * 본인 가입질문-답변 조회 (현재 질문 + 기존 답변 LEFT JOIN)
   * GET /api/club/{clubCode}/my-join-qna
   */
  getMyJoinQna: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: Array<{
        questionSeq: number
        answerSeq: number | null
        questionText: string
        questionType: 'TEXT' | 'CHOICE' | 'MULTIPLE'
        requiredYn: 'Y' | 'N'
        answerText: string | null
        selectedOptionSeq: number | null
        selectedOptionSeqsStr: string | null
        selectedOptionSeqs: number[] | null
        optionList: Array<{ optionSeq: number; optionText: string; optionOrder: number }>
      }>
    }>(`/api/club/${clubCode}/my-join-qna`)
    return response.data
  },

  /**
   * 본인 가입질문 답변 수정/추가
   * PUT /api/club/{clubCode}/my-join-qna
   */
  updateMyJoinQna: async (
    clubCode: string,
    answers: Array<{ questionSeq: number; answerText?: string; selectedOptionSeq?: number }>
  ) => {
    const response = await apiClient.put<{ code: number; message: string }>(
      `/api/club/${clubCode}/my-join-qna`,
      { answers }
    )
    return response.data
  },

  /**
   * 총무가 강제로 동호회 회원을 탈퇴
   * @param clubCode 동호회 인코딩 코드
   * @param clubMemberSeq
   */
  removeMember: async (clubCode: string, clubMemberSeq: number)=> {
    const response = await apiClient.delete(`/api/club/${clubCode}/members/${clubMemberSeq}`)
    return response.data
  },

  /**
   * 4차발주 D-4(p58): 회원 핸디 일괄 저장 (총무·부총무 전용)
   * 기존 회원은 핸디가 비어 있어 조편성 배지가 채워지지 않으므로 총무가 한 번에 입력한다.
   * handicap 이 빈 값이면 해당 회원은 NULL 로 지운다.
   */
  updateMemberHandicapBatch: async (
    clubCode: string,
    items: Array<{
      clubMemberSeq: number
      handicap: number | string | null
      /** 'M'|'F' — 빈 값이면 기존 값 유지 */
      gender?: string
      /** YYYYMMDD — 빈 값이면 기존 값 유지 */
      birthday?: string
    }>
  ) => {
    const response = await apiClient.put<{
      code: number
      message: string
      data?: { updated: number }
    }>(`/api/club/${clubCode}/members/handicap-batch`, items)
    return response.data
  },

  // ===== 4차발주 C-6(통합본 p36): 동호회 소개 첨부 — 파일올리기·이미지올리기 =====

  /** 소개 첨부 목록 (비회원 포함 열람 — 소개 화면은 VISITOR 에게도 노출) */
  /**
   * 골총비 기본 대표 이미지 목록 (공개) — 2026-09-29 발주 슬라이드 15.
   * 고르면 fileInfoSeq 를 그대로 clubImageSeq 로 저장한다(서버가 파일을 옮기지 않는다).
   */
  getDefaultImages: async () => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubDefaultImage[]
    }>('/api/club/default-images')
    return response.data
  },

  getIntroAttachments: async (clubCode: string) => {
    const response = await apiClient.get<{
      code: number
      message: string
      data: ClubIntroAttachment[]
    }>(`/api/club/${clubCode}/intro/attachments`)
    return response.data
  },

  /** 소개 첨부 업로드 (총무·부총무만) */
  uploadIntroAttachment: async (clubCode: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const response = await apiClient.post<{
      code: number
      message: string
      data?: { fileInfoSeq: number; fileOriNm: string; gubun: string }
    }>(`/api/club/${clubCode}/intro/attachment`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  /** 소개 첨부 삭제 (공용 파일 API 재사용) */
  deleteIntroAttachment: async (fileInfoSeq: number) => {
    const response = await apiClient.delete<{ code: number; message: string }>(
      `/api/file/${fileInfoSeq}`
    )
    return response.data
  },
}

/** 참가 특별요청 한 건(회원 상세·마이페이지) — 서버 열 이름 그대로 */
export interface EventSpecialRequestRow {
  EVENT_SEQ: number
  EVENT_NAME?: string
  EVENT_DATE?: string
  PARTICIPANT_SEQ: number
  SPECIAL_REQUEST: string
  SPECIAL_REQUEST_DATE?: string
  SPECIAL_REQUEST_STATUS?: string
}

/** 총무 특별참고사항 한 건 */
export interface MemberNote {
  noteSeq: number
  content: string
  regDate?: string
  register?: string
  registerName?: string
  updDate?: string | null
}

/** 회원 «특별사항» 묶음 — 2026-10-03 94·96번 장 */
export interface MemberSpecialNotes {
  joinRequest: { content?: string | null; date?: string | null }
  notes: MemberNote[]
  eventRequests: EventSpecialRequestRow[]
}

/** 마이페이지 «동호회 가입현황» 한 줄 — 98번 장 */
export interface MyClubDashboardItem {
  clubCode: string
  clubName: string
  nickname?: string | null
  memberRole?: string | null
  /** 회원이 적은 핸디(질문 30 ①) */
  handicap?: number | null
  joinRequest: { content?: string | null; date?: string | null }
  eventRequests: EventSpecialRequestRow[]
}
